import React, { useState, ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { useFlag } from "@unleash/proxy-client-react";
import { I18N_NAMESPACES, UNLEASH_TOPUP_V2 } from "../../../helpers/constants";
import { MemberShoppingCart } from "../../../types/__generated__/MemberShoppingCart";
import { MemberRewardBasicInfo } from "../../../types/__generated__/MemberRewardBasicInfo";
import {
  BadgeRelatedWines_badgeRelatedWines_badgeRecommendedWines as RecommendedWine,
  BadgeRelatedWines_badgeRelatedWines_badgeAlreadyDrunkWines as PastWine,
} from "../../../types/__generated__/BadgeRelatedWines";
import { formatDate } from "../../../helpers/dates";
import AddToBoxOrCartButton from "../AddToBoxOrCartButton";
import Loading from "../../atoms/Loading";
import WineCardCellarRating from "../../atoms/WineCardCellarRating";
import {
  trackingEvents,
  trackingEventWineRecommendationOptions,
  useTrackEvent,
} from "../../../helpers/Analytics";
import { getCashAmountLabel } from "../../../helpers/i18n";
import getBuyAgainCtaLabel from "../../../helpers/getBuyAgainCtaLabel";
import {
  CardContainer,
  InnerCardContainer,
  PercentMatch,
  CardContent,
  WineClassText,
  WineNameText,
  BottleImage,
  BottleTitle,
  RegionText,
  AddText,
  PriceLabel,
  StyledAddWineToShoppingCartButton,
  PastWinesOrderedDateTag,
  PastWineRatingLoadingStyle,
  PastWineRatingSpinnerStyle,
} from "./style";

interface ChallengeRecommendedWineCardProps {
  index: number;
  isRecommendedWine: boolean | false;
  memberShoppingCartQuery: MemberShoppingCart | MemberRewardBasicInfo | null | undefined;
  wineObject: RecommendedWine | PastWine | null;
  onWineDetailsClick: Function;
  onTopUpClick?: Function;
  shouldHideAddButton?: boolean;
  isInChallenges?: boolean;
}

const ChallengeRecommendedWineCard = ({
  index,
  memberShoppingCartQuery,
  isRecommendedWine = false,
  wineObject,
  onWineDetailsClick,
  onTopUpClick = () => {},
  shouldHideAddButton = false,
  isInChallenges = false,
}: ChallengeRecommendedWineCardProps) => {
  const { memberLikelihood, country, wineRegion, wineType, product, hasStock, hasOtherVintageStock } =
    wineObject || {};
  const wineClassName = wineType?.name ?? "";
  const wineName = product?.name ?? "";
  const photoSmall = product?.productPhotos?.[0]?.photoSmall;
  const coverPhotoSmall = product?.coverPhotoSmall;
  const bottleUrl = photoSmall || coverPhotoSmall || "";
  const countryName = country?.name ?? "";
  const regionName = wineRegion?.name ?? "";

  const { t } = useTranslation([I18N_NAMESPACES.COMMON]);
  const isTopupV2Enabled = useFlag(UNLEASH_TOPUP_V2);
  const [isPastWineRatingLoading, setIsPastWineRatingLoading] = useState<boolean>(false);
  const trackEvent = useTrackEvent();

  const isIndexEven = index % 2 === 0;

  const shouldShowAddToCartOrBoxButton = memberShoppingCartQuery && isRecommendedWine && !shouldHideAddButton;

  const buyAgainCtaLabel = getBuyAgainCtaLabel({
    hasStock,
    hasOtherVintageStock,
    t,
    isTopupV2Enabled,
  });

  const onWineCardClick = () => {
    onWineDetailsClick(wineObject?.product?.slug);
  };

  const onTopUpPastWineClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    onTopUpClick(wineObject);
  };

  /**
   * Renders percent match for recommended wines, and Top up link for past wines.
   * */
  const renderPercentMatchAndTopUp = (): ReactElement => {
    if (isRecommendedWine) {
      const matchPercentage = memberLikelihood ? Math.round(memberLikelihood * 100) : 0;
      const percentMatchText =
        matchPercentage > 80
          ? t("common:match", { memberLikelihoodAsPercentage: matchPercentage })
          : t("common:showDetails");
      return <PercentMatch>{percentMatchText}</PercentMatch>;
    }
    return <PercentMatch onClick={(event) => onTopUpPastWineClick(event)}>{buyAgainCtaLabel}</PercentMatch>;
  };

  /**
   * Renders ordered date tag got past wines card.
   * */
  const renderOrderedDate = (): ReactElement => {
    const orderedDate = `${formatDate(
      wineObject?.product?.lastOrderByMember?.createdDate ?? "",
      "DD MMM YY"
    )}`;
    const orderedDateText = t("common:orderedOnDate", { orderedDate });
    return <PastWinesOrderedDateTag>{orderedDateText}</PastWinesOrderedDateTag>;
  };

  const trackRecommendedWineAddedToBoxOrCart = () => {
    const trackingEvent = isInChallenges
      ? trackingEvents.TRACKING_EVENT_ADDED_WINE_RECOMMENDATION_CHALLENGES
      : trackingEvents.TRACKING_EVENT_ADDED_WINE_RECOMMENDATION_BADGES;

    const wineGrapeType = wineType?.name ?? "";
    const wineGrapeTypeId = wineType?.id ?? "";
    const wineColor = wineType?.wineClass?.name ?? "";
    const wineColorId = wineType?.wineClass?.id ?? "";
    const winePrice = wineObject?.product?.sellingPrice ?? "";

    trackEvent(trackingEvent, {
      [trackingEventWineRecommendationOptions.WINE_GRAPE_TYPE]: wineGrapeType,
      [trackingEventWineRecommendationOptions.WINE_GRAPE_TYPE_ID]: wineGrapeTypeId,
      [trackingEventWineRecommendationOptions.WINE_COLOUR]: wineColor,
      [trackingEventWineRecommendationOptions.WINE_COLOUR_ID]: wineColorId,
      [trackingEventWineRecommendationOptions.WINE_NAME]: wineName,
      [trackingEventWineRecommendationOptions.WINE_PRICE]: winePrice,
    });
  };

  /**
   * Renders add to box or cart button in recommended wines.
   * */
  const renderAddToBoxOrCartButton = (): ReactElement => {
    let wine = wineObject || {};
    wine = { ...wine, quantity: 1 };
    return (
      <AddToBoxOrCartButton
        wine={wine}
        ButtonComponent={StyledAddWineToShoppingCartButton}
        memberShoppingCartQuery={memberShoppingCartQuery}
        label=""
        submittingLabel=""
        extraCallback={() => trackRecommendedWineAddedToBoxOrCart()}
      >
        <AddText>{t("common:add")}</AddText>
        <PriceLabel>{getCashAmountLabel(wineObject?.product?.sellingPrice)}</PriceLabel>
      </AddToBoxOrCartButton>
    );
  };

  /**
   * Renders the past wine rating section.
   * */
  const renderPastWineRating = (): ReactElement => (
    <>
      {isPastWineRatingLoading ? (
        <Loading style={PastWineRatingLoadingStyle} spinnerStyle={PastWineRatingSpinnerStyle} />
      ) : (
        <WineCardCellarRating
          textAlign="center"
          rating={wineObject?.memberWineRatingScore}
          onClickRating={() => setIsPastWineRatingLoading(false)}
        />
      )}
    </>
  );

  return (
    <CardContainer isIndexEven={isIndexEven}>
      <InnerCardContainer>
        <CardContent onClick={() => onWineCardClick()}>
          {renderPercentMatchAndTopUp()}
          <WineClassText>{wineClassName}</WineClassText>
          <WineNameText addBottomMargin={!!isRecommendedWine}>{wineName}</WineNameText>
          {!isRecommendedWine && renderOrderedDate()}
          <BottleImage src={bottleUrl} alt={wineName} />
          <BottleTitle>{countryName}</BottleTitle>
          <RegionText addBottomMargin={!isRecommendedWine}>{regionName}</RegionText>
          {!isRecommendedWine && renderPastWineRating()}
        </CardContent>
      </InnerCardContainer>
      {shouldShowAddToCartOrBoxButton && renderAddToBoxOrCartButton()}
    </CardContainer>
  );
};

export default ChallengeRecommendedWineCard;
